import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '@/config/api';
import {
  Building2,
  MapPin,
  Briefcase,
  Home,
  Clock,
  IndianRupee,
  CalendarDays,
  CheckSquare,
  Star,
  Flame,
  Sparkles,
  Lightbulb,
  GraduationCap,
  Award,
  Plane,
  School,
  Gift,
  FileText,
  AlertTriangle,
  Loader2,
  X,
} from 'lucide-react';
import { formatSalaryDisplay } from '@/CompanyAdmin/utils/salaryUtils';

import {
  Dialog,
  DialogContent,
  DialogFooter,
} from '@/Components/ui/dialog';
import { Button } from '@/Components/ui/button';
import { Badge } from '@/Components/ui/badge';
import { Skeleton } from '@/Components/ui/skeleton';
import { ScrollArea } from '@/Components/ui/scroll-area';
import { Separator } from '@/Components/ui/separator';
import { cn } from '@/lib/utils';

/**
 * JobDescriptionModal
 *
 * Read-only JD viewer. Renders the full server response as sectioned
 * markdown-ish HTML (the server returns sanitized HTML for the long-text
 * fields like overview/responsibilities/qualifications).
 */
const JobDescriptionModal = ({ jobId, recruiterEmail, onClose }) => {
  const [jobData, setJobData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchJobDetails = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams({ jobId, recruiterEmail });
        const res = await fetch(
          `${API_BASE_URL}/api/recruiter/job-full-details?${params}`,
          { credentials: 'include' },
        );
        if (!res.ok) throw new Error('Failed to fetch job details');
        const data = await res.json();
        if (!cancelled) setJobData(data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchJobDetails();
    return () => {
      cancelled = true;
    };
  }, [jobId, recruiterEmail]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-4xl p-0">
        {loading && <LoadingShell />}
        {!loading && error && <ErrorShell error={error} onClose={onClose} />}
        {!loading && !error && jobData && (
          <JobView jobData={jobData} onClose={onClose} />
        )}
      </DialogContent>
    </Dialog>
  );
};

// =============================================================================
// Sub-views
// =============================================================================

function LoadingShell() {
  return (
    <div className="space-y-4 p-6">
      <Skeleton className="h-6 w-1/3" />
      <Skeleton className="h-10 w-2/3" />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-24 w-full" />
    </div>
  );
}

function ErrorShell({ error, onClose }) {
  return (
    <div className="flex flex-col items-center gap-3 p-8 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
        <AlertTriangle className="h-6 w-6" />
      </div>
      <h3 className="text-lg font-semibold">Error loading job</h3>
      <p className="text-sm text-muted-foreground">{error}</p>
      <Button size="sm" onClick={onClose}>
        Close
      </Button>
    </div>
  );
}

function JobView({ jobData, onClose }) {
  return (
    <>
      {/* Sticky header */}
      <div className="sticky top-0 z-10 border-b border-border/40 bg-card px-6 py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex items-center gap-1.5 text-xs text-muted-foreground">
              <Building2 className="h-3 w-3" />
              <span>{jobData.company || 'Company'}</span>
            </div>
            <h1 className="text-xl font-bold tracking-tight">{jobData.title}</h1>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Meta row */}
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-xs text-muted-foreground">
          <MetaItem icon={MapPin} text={jobData.location} />
          <MetaItem icon={Briefcase} text={jobData.employment_type} />
          <MetaItem icon={Home} text={jobData.job_type} />
          <MetaItem
            icon={Clock}
            text={
              jobData.experience_range ||
              `${jobData.min_exp}-${jobData.max_exp} years`
            }
          />
          {(() => {
            const salaryLabel = formatSalaryDisplay(jobData.salary_range);
            return salaryLabel !== 'Not specified' ? (
              <MetaItem icon={IndianRupee} text={salaryLabel} />
            ) : null;
          })()}
        </div>

        {/* Stats bar */}
        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Stat label="Applications" value={jobData.stats.total_applications} />
          <Stat
            label="Hired"
            value={jobData.stats.hired}
            valueClass="text-emerald-600 dark:text-emerald-400"
          />
          <Stat
            label="Shortlisted"
            value={jobData.stats.shortlisted}
            valueClass="text-blue-600 dark:text-blue-400"
          />
          <Stat
            label="Rejected"
            value={jobData.stats.rejected}
            valueClass="text-rose-600 dark:text-rose-400"
          />
        </div>
      </div>

      {/* Body */}
      <ScrollArea className="max-h-[60vh] px-6 py-4">
        <div className="space-y-6">
          {jobData.job_overview && (
            <Section icon={CheckSquare} title="Job Overview">
              <RichHTML html={jobData.job_overview} />
            </Section>
          )}

          {jobData.key_responsibilities && (
            <Section icon={CheckSquare} title="Key Responsibilities">
              <RichHTML html={jobData.key_responsibilities} />
            </Section>
          )}

          {/* Skills */}
          <Section icon={Star} title="Skills">
            <div className="space-y-3">
              <SkillRow
                label="Required"
                icon={Star}
                color="blue"
                skills={jobData.required_skills}
              />
              {jobData.critical_skills?.length > 0 && (
                <SkillRow
                  label="Critical"
                  icon={Flame}
                  color="rose"
                  skills={jobData.critical_skills}
                />
              )}
              {jobData.good_to_have_skills?.length > 0 && (
                <SkillRow
                  label="Good to Have"
                  icon={Sparkles}
                  color="emerald"
                  skills={jobData.good_to_have_skills}
                />
              )}
              {jobData.soft_skills?.length > 0 && (
                <SkillRow
                  label="Soft Skills"
                  icon={Lightbulb}
                  color="violet"
                  skills={jobData.soft_skills}
                />
              )}
            </div>
          </Section>

          {jobData.required_qualifications && (
            <Section icon={GraduationCap} title="Required Qualifications">
              <RichHTML html={jobData.required_qualifications} />
            </Section>
          )}

          {jobData.preferred_qualifications && (
            <Section icon={Award} title="Preferred Qualifications">
              <RichHTML html={jobData.preferred_qualifications} />
            </Section>
          )}

          {/* Info cards row */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {jobData.work_hours && (
              <InfoCard
                icon={Clock}
                label="Work Hours"
                value={jobData.work_hours}
                tone="blue"
              />
            )}
            {jobData.travel_requires && (
              <InfoCard
                icon={Plane}
                label="Travel"
                value={jobData.travel_requires}
                tone="violet"
              />
            )}
            {jobData.application_deadline && (
              <InfoCard
                icon={CalendarDays}
                label="Application Deadline"
                value={new Date(
                  jobData.application_deadline,
                ).toLocaleDateString()}
                tone="rose"
              />
            )}
            {jobData.colleges_applicable && (
              <InfoCard
                icon={School}
                label="Colleges"
                value={jobData.colleges_applicable}
                tone="emerald"
              />
            )}
          </div>

          {jobData.benefits_perks && (
            <Section icon={Gift} title="Benefits & Perks" highlight>
              <RichHTML html={jobData.benefits_perks} />
            </Section>
          )}

          {jobData.company_overview && (
            <Section icon={Building2} title="About the Company">
              <RichHTML html={jobData.company_overview} />
            </Section>
          )}

          {jobData.how_to_apply && (
            <Section icon={FileText} title="How to Apply">
              <RichHTML html={jobData.how_to_apply} />
            </Section>
          )}
        </div>
      </ScrollArea>

      {/* Footer */}
      <Separator />
      <DialogFooter className="flex w-full flex-wrap items-center justify-between gap-2 px-6 py-3 sm:flex-nowrap">
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <CalendarDays className="h-3 w-3" />
          Posted on{' '}
          {new Date(jobData.created_at).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </span>
        <Button onClick={onClose}>Close</Button>
      </DialogFooter>
    </>
  );
}

// =============================================================================
// Tiny presentational helpers
// =============================================================================

function MetaItem({ icon: Icon, text }) {
  if (!text) return null;
  return (
    <span className="flex items-center gap-1.5">
      <Icon className="h-3.5 w-3.5" />
      <span>{text}</span>
    </span>
  );
}

function Stat({ label, value, valueClass }) {
  return (
    <div className="rounded-md border border-border/40 bg-card px-3 py-2 text-center">
      <div className={cn('text-lg font-bold tabular-nums', valueClass)}>
        {value ?? 0}
      </div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, children, highlight = false }) {
  return (
    <section
      className={cn(
        'rounded-lg border border-border/40 bg-card p-4',
        highlight && 'border-primary/30 bg-primary/5',
      )}
    >
      <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold">
        <Icon className="h-4 w-4 text-primary" />
        {title}
      </h2>
      <div className="text-sm leading-relaxed text-foreground/90">
        {children}
      </div>
    </section>
  );
}

function RichHTML({ html }) {
  // Server returns sanitized HTML. We trust it here because the source is
  // our own backend, which has already escaped/cleaned user input.
  if (!html) return null;
  return (
    <div
      className="prose prose-sm max-w-none dark:prose-invert prose-p:my-2 prose-li:my-1"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function SkillRow({ label, icon: Icon, color, skills }) {
  const tones = {
    blue: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400',
    rose: 'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400',
    emerald:
      'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400',
    violet:
      'bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-400',
  };
  if (!skills || skills.length === 0) {
    return (
      <div className="space-y-1">
        <h3 className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
          <Icon className="h-3 w-3" />
          {label}
        </h3>
        <p className="text-xs text-muted-foreground/80">Not specified</p>
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      <h3 className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
        <Icon className="h-3 w-3" />
        {label}
      </h3>
      <div className="flex flex-wrap gap-1.5">
        {skills.map((skill, idx) => (
          <Badge
            key={idx}
            variant="outline"
            className={cn('border-0 text-xs', tones[color])}
          >
            {skill}
          </Badge>
        ))}
      </div>
    </div>
  );
}

function InfoCard({ icon: Icon, label, value, tone = 'blue' }) {
  const tones = {
    blue: 'bg-blue-100 text-blue-600 dark:bg-blue-950/40 dark:text-blue-400',
    violet:
      'bg-violet-100 text-violet-600 dark:bg-violet-950/40 dark:text-violet-400',
    rose: 'bg-rose-100 text-rose-600 dark:bg-rose-950/40 dark:text-rose-400',
    emerald:
      'bg-emerald-100 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400',
  };
  return (
    <div className="flex items-center gap-3 rounded-md border border-border/40 bg-card p-3">
      <div
        className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
          tones[tone],
        )}
      >
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
          {label}
        </div>
        <div className="truncate text-sm font-medium">{value}</div>
      </div>
    </div>
  );
}

export default JobDescriptionModal;
